import { useEffect, useState } from 'react';

import { api } from '../api/endpoints';
import type { PersonDetail } from '../api/types';
import { LOOKING_FOR_LABELS } from '../api/types';
import type { Medal } from '../api/types';
import { MedalRow } from '../components/MedalRow';

interface Props {
  userId: number;
  onBack: () => void;
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

/** Ссылка на человека: @ник → t.me, иначе как есть. */
function contactHref(raw: string): string | null {
  const value = raw.trim();
  if (value.startsWith('@')) return `https://t.me/${value.slice(1)}`;
  if (value.startsWith('http')) return value;
  if (/^\+?\d[\d\s()-]{6,}$/.test(value)) {
    return `https://wa.me/${value.replace(/\D/g, '')}`;
  }
  return null;
}

export function PersonPage({ userId, onBack }: Props) {
  const [person, setPerson] = useState<PersonDetail | null>(null);
  const [missing, setMissing] = useState(false);
  const [medals, setMedals] = useState<Medal[]>([]);

  useEffect(() => {
    api.person(userId).then(setPerson).catch(() => setMissing(true));
    api
      .medalsOf(userId)
      .then(setMedals)
      .catch(() => setMedals([]));
  }, [userId]);

  if (missing) {
    return (
      <div className="screen">
        <button className="back" onClick={onBack}>← Назад</button>
        <p className="muted">Профиль недоступен.</p>
      </div>
    );
  }

  if (!person) {
    return (
      <div className="screen">
        <button className="back" onClick={onBack}>← Назад</button>
        <div className="skeleton" />
      </div>
    );
  }

  const contact = person.telegram_link ?? person.contacts;
  const href = contact ? contactHref(contact) : null;

  // Сухие факты одним блоком — это и есть background, ради которого
  // человек открывает чужую карточку перед мероприятием.
  const facts: [string, string][] = [];
  if (person.occupation) facts.push(['Ниша', person.occupation]);
  if (person.company) facts.push(['Компания', person.company]);
  if (person.employees_count) facts.push(['Команда', person.employees_count]);
  if (person.age) facts.push(['Возраст', String(person.age)]);
  if (person.monthly_income_usd) {
    facts.push(['Доход в месяц', `${person.monthly_income_usd} $`]);
  }

  return (
    <div className="screen screen--detail">
      <button className="back" onClick={onBack}>← Назад</button>

      <div className="profile-head">
        {person.photo_url ? (
          <img className="profile-head__avatar" src={person.photo_url} alt="" />
        ) : (
          <div className="profile-head__avatar profile-head__avatar--letters">
            {initials(person.full_name)}
          </div>
        )}
        <div>
          <h1 className="profile-head__name">{person.full_name}</h1>
          {(person.occupation || person.company) && (
            <div className="muted">
              {[person.occupation, person.company].filter(Boolean).join(' · ')}
            </div>
          )}
        </div>
      </div>

      {/* Сразу под именем и без заголовка: это первое, что человек
          прочитает, и повод, с которого начнётся разговор. */}
      {person.fun_fact && <p className="detail__fact">{person.fun_fact}</p>}

      <MedalRow medals={medals} />

      {person.looking_for_tags.length > 0 && (
        <section className="block">
          <h2 className="block__title">В поиске</h2>
          <div className="person__tags">
            {person.looking_for_tags.map((tag) => (
              <span key={tag} className="tag tag--strong">
                {LOOKING_FOR_LABELS[tag] ?? tag}
              </span>
            ))}
          </div>
        </section>
      )}

      {facts.length > 0 && (
        <section className="block">
          <h2 className="block__title">О бизнесе</h2>
          <dl className="facts">
            {facts.map(([label, value]) => (
              <div className="fact" key={label}>
                <dt>{label}</dt>
                <dd>{value}</dd>
              </div>
            ))}
          </dl>
        </section>
      )}

      {person.can_offer && (
        <section className="block">
          <h2 className="block__title">Чем может быть полезен</h2>
          <p className="detail__text">{person.can_offer}</p>
        </section>
      )}

      {person.looking_for && !person.looking_for_tags.length && (
        <section className="block">
          <h2 className="block__title">Что ищет</h2>
          <p className="detail__text">{person.looking_for}</p>
        </section>
      )}

      {person.offer && (
        <section className="block">
          <h2 className="block__title">Оффер</h2>
          <p className="detail__text">{person.offer}</p>
        </section>
      )}

      {person.interests.length > 0 && (
        <section className="block">
          <h2 className="block__title">Интересы</h2>
          <div className="person__tags">
            {person.interests.map((item) => (
              <span key={item} className="tag">{item}</span>
            ))}
          </div>
        </section>
      )}

      {person.website && (
        <section className="block">
          <h2 className="block__title">Соцсети и сайт</h2>
          {person.website.startsWith('http') ? (
            <a className="link" href={person.website} target="_blank" rel="noreferrer">
              {person.website}
            </a>
          ) : (
            <p className="detail__text">{person.website}</p>
          )}
        </section>
      )}

      {contact && (
        <div className="sticky-action">
          {href ? (
            <a className="btn" href={href} target="_blank" rel="noreferrer">
              Написать
            </a>
          ) : (
            <div className="action-note">{contact}</div>
          )}
        </div>
      )}
    </div>
  );
}
